import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isWithinInterval from 'date-fns/isWithinInterval';
import subHours from 'date-fns/subHours';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import { Icon } from '@dnb/eufemia/components';
import FindLanguage from '../../i18n_custom';
import Card from '@dnb/eufemia/components/card/Card';
import {getIncidentStatusIcon } from '../../helpers';

import './incident-viewer.css';
import { useTranslation } from 'react-i18next';

/**
 * IncidentViewer - A reusable component for displaying incidents with different filter types
 * 
 * @param {Array} incidents - Array of all incidents
 * @param {String} filterType - Type of filter: 'ongoing', 'historic', 'hours', 'days'
 * @param {Number} timeRange - Number of hours/days for time-based filters
 * @param {String} title - Optional title for the section
 * @param {String} className - Optional additional CSS class
 */
export default function IncidentViewer({ 
  incidents = [], 
  filterType = 'ongoing',
  timeRange = 48,
  title,
  className = ''
}) {
  const { t, i18n } = useTranslation();
  const [filteredIncidents, setFilteredIncidents] = useState([]);

  // Helper function to handle bilingual text (Norwegian||English)
const FindLanguage = (incidentText) => {
  console.log('Language: ' + i18n.language);
  let incidentTextInLanguage = incidentText;
  let splitter = incidentText.indexOf('||');

  // If we find splitter in the text
  if (splitter !== -1) {
    console.log('Found language splitter');
    // If the detected language is norwegian we select the left side of the string
    if (i18n.language === 'nb-NO' || i18n.language === 'no' || i18n.language === 'nb') {
      incidentTextInLanguage = incidentText.substring(0, splitter);
    }
    // If it is not norwegian we select the right side of the string (starting 2 characters || to the right)
    else {
      incidentTextInLanguage = incidentText.substring(2 + splitter);
    }
    // Else we do not find a splitter, and we return the string untouched.
  } else {
    console.log('Did not find language splitter for text: -->' + incidentTextInLanguage + '<--');
    //console.log('With translation it is: ' + t(incidentTextInLanguage));
  }
  console.log('Returning this text: ' + t(incidentTextInLanguage));
  return t(incidentTextInLanguage);
};

  // Helper function to get status text - only four options
  const getStatusText = (status) => {
    switch (status) {
      case 'operational':
      case 'none':
        return t('operational');
      case 'minor_outage':
      case 'partial_outage':
      case 'minor':  
        return t('minor');
      case 'major_outage':
      case 'critical':
      case 'major':  
        return t('major');
      case 'degraded_performance':
        return t('degraded_performance');
      default:
        return status;
    }
  };

  useEffect(() => {
    if (!incidents || !Array.isArray(incidents)) {
      setFilteredIncidents([]);
      return;
    }

    let filtered = [];

    switch (filterType) {
      case 'ongoing':
        // Only unresolved incidents
        filtered = incidents.filter((e) => e.status !== 'resolved');
        break;

      case 'historic':
        // Only resolved incidents
        filtered = incidents.filter((e) => e.status === 'resolved');
        break;

      case 'hours':
        // Incidents within the last X hours
        {
          let now = new Date();
          let earliestDate = subHours(now, timeRange);
          filtered = incidents.filter((e) => {
            if (e.status !== 'resolved') {
              return true;
            } else {
              return isWithinInterval(new Date(e.updated_at), { 
                start: earliestDate, 
                end: now 
              });
            }
          });
        }
        break;

      case 'days':
        // Incidents within the last X days
        {
          let now = new Date();
          let earliestDate = subDays(now, timeRange);
          filtered = incidents.filter((e) => {
            if (e.status !== 'resolved') {
              return true;
            } else {
              return isWithinInterval(new Date(e.updated_at), { 
                start: earliestDate, 
                end: now 
              });
            }
          });
        }
        break;

      case 'all':
        // All incidents
        filtered = incidents;
        break;

      default:
        filtered = incidents.filter((e) => e.status !== 'resolved');
    }

    setFilteredIncidents(filtered);
  }, [incidents, filterType, timeRange]);

  const renderIncidentCard = (incident) => {
    try {
      const startedAt = incident.started_at ? format(new Date(incident.started_at), 'dd.MM.yyyy, HH:mm') : 'N/A';
      const latestUpdate = incident.incident_updates && incident.incident_updates.length > 0 
        ? incident.incident_updates[0] 
        : null;

      // Get affected components
      const affectedComponents = incident.components && incident.components.length > 0
        ? incident.components.map(c => t(c.name || c)).join(', ')
        : '';

      const impactStatus = incident.impact || 'none';

      return (
        <Card 
          key={incident.id} 
          className={"incident-viewer__card"} 
          outline='none'
        >
          <div className="incident-viewer__card-content">
            <div className="incident-viewer__card-header">
              <div className="incident-viewer__card-start">
                <span className="incident-viewer__label">{t('Incident started')}: {startedAt}</span>
              </div>
            </div>

            <div className="incident-viewer__right-section">
              <div className={`incident-viewer__status-container incident-viewer__status-container--${impactStatus}`}>
                <span className={`incident-viewer__icon-circle incident-viewer__icon-circle--${impactStatus}`}>
                  <Icon icon={getIncidentStatusIcon(impactStatus)} size="16" />
                </span>
                <span className={`incident-viewer__status-text`}>
                  {getStatusText(impactStatus)}
                </span>
              </div>
            </div>

            <h3 className="incident-viewer__title">{incident.name ? FindLanguage(incident.name) : 'Incident'}</h3>

            {affectedComponents && (
              <div className={`incident-viewer__affected incident-viewer__affected--${impactStatus}`}>
                <span className="incident-viewer__affected-label">{t('Incident affects')}: {affectedComponents}</span>
              </div>
            )}

            {latestUpdate && latestUpdate.body && (
              <div className="incident-viewer__description">
                {FindLanguage(latestUpdate.body)}
              </div>
            )}

            
          </div>
        </Card>
      );
    } catch (error) {
      console.error('Error rendering incident card:', error, incident);
      return null;
    }
  };

  return (
    <div className={`incident-viewer ${className}`}>
      {title && (
        <h2 className="incident-viewer__section-title">{title}</h2>
      )}
      
      {filteredIncidents.length > 0 ? (
        <div className="incident-viewer__list">
          {filteredIncidents.map(incident => renderIncidentCard(incident))}
        </div>
      ) : (
        <Card 
          className="incident-viewer__empty-card" 
          outline='none'
          backgroundColor="pistachio"
        >
          <div className="incident-viewer__empty-content">
            <h1 className="incident-viewer__empty-title">
              {filterType === 'ongoing' 
                ? t('All systems are currently operating normally')
                : t('No resolved incidents in the archive')
              }
            </h1>
          </div>
        </Card>
      )}
    </div>
  );
}

IncidentViewer.propTypes = {
  incidents: PropTypes.array,
  filterType: PropTypes.oneOf(['ongoing', 'historic', 'hours', 'days', 'all']),
  timeRange: PropTypes.number,
  title: PropTypes.string,
  className: PropTypes.string,
};
